/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { memo, useEffect, useState } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import { cssMap, jsx } from '@atlaskit/css';
import Image from '@atlaskit/image';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import { AIEventsInstrumentationProvider } from '@atlassian/ai-analytics/ai-events-instrumentation-provider';

import { useSmartAnswerCardFeedbackOptions } from '../../../ai-answer-dialog/smart-answer-card/use-smart-answer-card-feedback-options';
import { useAnalytics } from '../../../common/controller/analytics/use-analytics';
import { AIFeedbackV2 } from '../../../common/ui/ai-feedback-v2';
import { ErrorBoundary } from '../../../common/ui/error-boundary';
import { AiExperienceName, DEFAULT_GAS_CHANNEL } from '../../../constants';
import { sha256Hash } from '../../../sha256-hash';
import { AddPinModal } from './add-pin/add-pin-modal';
import SuggestionSearch from './assets/bookmark-suggestion-search-icon.svg';
import { messages } from './messages';
import { onPopularSearchQueryButtonClicked } from './on-popular-search-query-button-clicked';
import { onPopularSearchQuerySuggestionShown } from './on-popular-search-query-suggestion-shown';

const styles = cssMap({
	bookmarkSuggestionContainer: {
		backgroundColor: token('color.background.input'),
		borderRadius: token('radius.small'),
		boxShadow: token('elevation.shadow.overflow'),
		display: 'flex',
		gap: token('space.100'),
		paddingBlock: token('space.250'),
		paddingInline: token('space.250'),
	},
	bookmarkSuggestionIconContainer: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		paddingInline: token('space.150'),
	},
	bookmarkSuggestionContentContainer: {
		width: '100%',
	},
});

type BookmarkSuggestionCardProps = {
	suggestedTerm: string;
	cloudId: string;
	orgId: string;
	hasAdminhubAccess: boolean;
	workspaceId: string;
	product: string;
	searchSessionId: string;
	userDetails: {
		name?: string;
		id?: string;
	};
	source: string;
};

const BookmarkSuggestionCardComponent = memo(
	({
		suggestedTerm,
		cloudId,
		orgId,
		hasAdminhubAccess,
		workspaceId,
		product,
		searchSessionId,
		userDetails,
		source,
	}: BookmarkSuggestionCardProps) => {
		const { formatMessage } = useIntl();
		const [fireAnalyticsEvent] = useAnalytics();

		const [showAddModal, setShowAddModal] = useState(false);
		const [isDismissed, setIsDismissed] = useState(false);

		useEffect(() => {
			fireAnalyticsEvent(onPopularSearchQuerySuggestionShown());
		}, [fireAnalyticsEvent]);

		const feedbackOptions = useSmartAnswerCardFeedbackOptions();
		const feedbackAttributes = {
			searchSessionId,
			source,
			queryHash: sha256Hash(suggestedTerm),
			cloudId,
		};

		const onBookmarkCreationSuccess = () => {
			setIsDismissed(true);
		};

		const onAddBookmarkClick = () => {
			setShowAddModal(true);
			fireAnalyticsEvent(onPopularSearchQueryButtonClicked());
		};

		return (
			<ErrorBoundary feature="bookmarks">
				{isDismissed ? null : (
					<Box xcss={styles.bookmarkSuggestionContainer}>
						<Box xcss={styles.bookmarkSuggestionIconContainer}>
							<Image src={SuggestionSearch} width="88" alt="" />
						</Box>
						<Box xcss={styles.bookmarkSuggestionContentContainer}>
							<Flex direction="column" gap="space.075">
								<Text size="large" weight="bold" testId="bookmark-suggestion-main-message">
									{formatMessage(messages.pinSuggestionMainMessage, {
										suggestedTerm: (
											<Text size="large" weight="bold" color="color.text.brand">
												{suggestedTerm}
											</Text>
										),
									})}
								</Text>
								<Stack space="space.250">
									<Text>{formatMessage(messages.pinSuggestionSubMessage)}</Text>
									<Inline alignBlock="center" spread="space-between">
										<Button appearance="default" onClick={onAddBookmarkClick}>
											{formatMessage(messages.add_pin_button_label)}
										</Button>

										<AIFeedbackV2
											entrypointId={feedbackOptions.entrypointId}
											userDetails={userDetails}
											attributes={feedbackAttributes}
											onLikeButtonClickHandler={feedbackOptions.onLikeButtonClickHandler}
											onDislikeButtonClickHandler={feedbackOptions.onDislikeButtonClickHandler}
											featureTitle={formatMessage(messages.userCreatedBookmarksFeatureTitle)}
										/>
									</Inline>
								</Stack>
							</Flex>
						</Box>
					</Box>
				)}
				{showAddModal && (
					<AddPinModal
						query={suggestedTerm}
						handleClose={() => setShowAddModal(false)}
						orgId={orgId}
						cloudId={cloudId}
						hasAdminhubAccess={hasAdminhubAccess}
						workspaceId={workspaceId}
						product={product}
						source="popularSearchQuery"
						onSuccessCallback={onBookmarkCreationSuccess}
					/>
				)}
			</ErrorBoundary>
		);
	},
);

export const BookmarkSuggestionCard: (props: BookmarkSuggestionCardProps) => JSX.Element = (
	props: BookmarkSuggestionCardProps,
) => (
	<AIEventsInstrumentationProvider
		config={{
			product: props.product,
			subproduct: 'search-page',
			aiFeatureName: 'smartAnswers',
			aiExperienceName: AiExperienceName.POPULAR_SEARCH_QUERY_CARD,
			proactiveGeneratedAI: 0,
			userGeneratedAI: 1,
			isAIFeature: 0,
			source: props.source,
			channel: DEFAULT_GAS_CHANNEL,
		}}
	>
		<BookmarkSuggestionCardComponent {...props} />
	</AIEventsInstrumentationProvider>
);
